import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Sidebar from '../../GlobalManagement/Pages/Sidebar';
import BackButton from "../../GlobalManagement/Pages/BackButton";
import MobileHeaderToggle from "../../GlobalManagement/Pages/MobileHeaderToggle";
import SearchBar from '../../GlobalManagement/Pages/SearchBar';
import Pagination from "../../GlobalManagement/Pages/Pagination";
import toast from "react-hot-toast";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterPrimaryButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterFormGridRow, 
  masterInputLabel, 
  masterInput, 
  masterSelect
} from '../../GlobalManagement/Pages/masterPageStyles';
import { Calendar, Download, Save ,Loader2} from "lucide-react";

const normalizeEmployeeId = (value) => String(value || "").trim().toUpperCase();
const isExEmployeeId = (value) => normalizeEmployeeId(value).startsWith("EX-");
const getEmployeePrefix = (value) => {
  const normalized = normalizeEmployeeId(value);
  if (!normalized || isExEmployeeId(normalized)) return "";
  return normalized.split("-")[0] || "";
};

const ShiftManagement = () => {
  const PER_PAGE_STORAGE_KEY = "shiftManagement.perPage";
  const getStoredPerPage = () => {
    const raw = localStorage.getItem(PER_PAGE_STORAGE_KEY);
    if (!raw) return 20;
    if (raw === "all") return "all";
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : 20;
  };

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const tableContainerRef = React.useRef(null); 
  const [shiftOptions, setShiftOptions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [selectedDesignation, setSelectedDesignation] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [shifts, setShifts] = useState({});
  const [lockedDaysByUserId, setLockedDaysByUserId] = useState({});
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(getStoredPerPage);
  const [departments, setDepartments] = useState([]); 
  const [selectedDepartment, setSelectedDepartment] = useState("ALL"); 
  const [selectedEmployeePrefix, setSelectedEmployeePrefix] = useState("ALL");
  const [deptWiseDesignations, setDeptWiseDesignations] = useState([]); 
  const [isDepartmentHeadScoped, setIsDepartmentHeadScoped] = useState(false);
  const [scopedDepartment, setScopedDepartment] = useState("");
  const [scopedDesignations, setScopedDesignations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizeCode = (value) => String(value || "").trim().toUpperCase();
  const normalizeSearchInput = (value) => {
    let v = String(value || "").toUpperCase();
    if (/^[A-Z]+\d/.test(v)) {
      v = v.replace(/^([A-Z]+)-?(\d.*)$/, "$1-$2");
    }
    return v;
  };

  const parseDDCodes = (value) => {
    const code = normalizeCode(value);
    if (!code.startsWith("DD:")) return ["", ""];
    const payload = code.slice(3);

    if (payload.includes("+")) {
      const [first = "", second = ""] = payload.split("+");
      return [first, second || first];
    }

    if (payload.length === 2) return [payload[0], payload[1]];
    return [payload, payload];
  };

  const encodeDDCode = (first, second) =>
    `DD:${normalizeCode(first)}+${normalizeCode(second || first)}`;

  useEffect(() => {
    localStorage.setItem(PER_PAGE_STORAGE_KEY, String(perPage));
  }, [perPage]);

  useEffect(() => {
    const loadDepartmentHeadScope = async () => {
      try {
        const adminData = JSON.parse(localStorage.getItem("adminData") || "{}");
        const possibleIds = [adminData?.employeeUserId, adminData?.userId]
          .filter(Boolean)
          .map((v) => String(v).trim());

        if (possibleIds.length === 0) return;

        const res = await axios.get("/api/department-heads");
        const records = Array.isArray(res.data) ? res.data : [];
        const matched = records.find((r) => possibleIds.includes(String(r.employeeUserId || "").trim()));
        if (!matched) return;

        const namesFromNew = Array.isArray(matched.designationData)
          ? matched.designationData.map((d) => d?.name).filter(Boolean)
          : [];
        const namesFromOld = Array.isArray(matched.designationArray) ? matched.designationArray : [];
        const uniqueDesignationNames = [...new Set([...namesFromNew, ...namesFromOld])];

        setIsDepartmentHeadScoped(true);
        setScopedDepartment(matched.departmentName || "");
        setScopedDesignations(uniqueDesignationNames);
        setSelectedDepartment(matched.departmentName || "ALL");
        setSelectedDesignation("ALL");
      } catch (err) {
        // Keep default unrestricted flow if scope fetch fails
      }
    };

    loadDepartmentHeadScope();
  }, []);

  useEffect(() => {
    const fetchShiftOptions = async () => {
      try {
        const res = await axios.get("/api/shifts"); 
        const options = res.data
          .filter(s => s.status === "Active")
          .map(s => ({
            code: normalizeCode(s.shiftCode), 
            name: s.shiftName,                     
            start: s.startTime,                    
            end: s.endTime                         
          }));

        setShiftOptions([
          ...options,
          { code: "OFF", name: "OFF" },
          { code: "OFF(EXCH)", name: "OFF(EXCH)" },
          { code: "DD", name: "Double Duty" }
        ]);
      } catch (err) {
        toast.error("Failed to fetch shift options");
      }
    };

    fetchShiftOptions();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await axios.get("/api/employees");
        setEmployees(res.data); 
      } catch (err) {
        toast.error("Failed to fetch employees");
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchDeptData = async () => {
      try {
        const res = await axios.get("/api/designations");
        setDeptWiseDesignations(res.data);
        const uniqueDepts = ["ALL", ...new Set(res.data.map(d => d.departmentName))];
        setDepartments(uniqueDepts);
        setDesignations(["ALL", ...res.data.map((d) => d.designationName)]);
      } catch (err) {
        toast.error("Dept/Designation fetch error");
      }
    };
    fetchDeptData();
  }, []);

  const displayDesignations = useMemo(() => {
    if (isDepartmentHeadScoped) {
      if (scopedDesignations.length > 0) {
        return ["ALL", ...scopedDesignations];
      }
      const scopedFallback = deptWiseDesignations
        .filter(d => d.departmentName === scopedDepartment)
        .map(d => d.designationName);
      return ["ALL", ...scopedFallback];
    }
    if (selectedDepartment === "ALL") {
      return ["ALL", ...deptWiseDesignations.map(d => d.designationName)];
    }
    const filtered = deptWiseDesignations
      .filter(d => d.departmentName === selectedDepartment)
      .map(d => d.designationName);
      
    return ["ALL", ...filtered];
  }, [selectedDepartment, deptWiseDesignations, isDepartmentHeadScoped, scopedDepartment, scopedDesignations]);

  const visibleDepartments = useMemo(() => {
    if (isDepartmentHeadScoped && scopedDepartment) return [scopedDepartment];
    return departments;
  }, [departments, isDepartmentHeadScoped, scopedDepartment]);

  const employeePrefixOptions = useMemo(() => {
    const prefixes = employees
      .map((emp) => getEmployeePrefix(emp.employeeID))
      .filter(Boolean);
    return ["ALL", ...new Set(prefixes)];
  }, [employees]);

  const nonDDShiftOptions = useMemo(
    () =>
      shiftOptions.filter(
        (o) =>
          normalizeCode(o.code) !== "OFF" &&
          normalizeCode(o.code) !== "OFF(EXCH)" &&
          normalizeCode(o.code) !== "DD"
      ),
    [shiftOptions]
  );

  useEffect(() => {
    const fetchShifts = async () => {
      try {
        if (shiftOptions.length === 0) return;
        const formatMonth = (ym) => {
          const [y, m] = ym.split("-");
          return new Date(y, m - 1).toLocaleString("en-US", { month: "short" }) + "-" + y;
        };
  
        const res = await axios.get(
          `/api/shift-management/${formatMonth(selectedMonth)}`
        );

        const formatted = {};
        const lockedMap = {};
        const validCodes = new Set(shiftOptions.map((opt) => normalizeCode(opt.code)));
        res.data.forEach((item) => {
          const processedShifts = {};
          Object.entries(item.shifts || {}).forEach(([day, rawCode]) => {
            const code = normalizeCode(rawCode);
            if (!code) return;

            if (code.startsWith("DD:")) {
              const [first, second] = parseDDCodes(code);
              processedShifts[day] = encodeDDCode(first, second);
            } else if (validCodes.has(code) || code === "OFF") {
              processedShifts[day] = code;
            } else if (code.length === 2) {
              processedShifts[day] = encodeDDCode(code[0], code[1]);
            } else {
              processedShifts[day] = code;
            }
          });
          formatted[item.employeeUserId] = processedShifts;
          lockedMap[item.employeeUserId] = Array.isArray(item.lockedDays) ? item.lockedDays : [];
        });
  
        setShifts(formatted);
        setLockedDaysByUserId(lockedMap);
      } catch (err) {
        setShifts({});
        setLockedDaysByUserId({});
      }
    };
    fetchShifts();
  }, [selectedMonth, shiftOptions]);

  const daysInMonth = useMemo(() => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const totalDays = new Date(year, month, 0).getDate();
    return Array.from({ length: totalDays }, (_, i) => i + 1);
  }, [selectedMonth]);

  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthMeta = useMemo(() => {
    const [year, month] = selectedMonth.split("-").map(Number);
    return { year, month };
  }, [selectedMonth]);

  const expiredDaysSet = useMemo(() => {
    if (!monthMeta.year || !monthMeta.month) return new Set();
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    if (monthMeta.year < currentYear || (monthMeta.year === currentYear && monthMeta.month < currentMonth)) {
      const totalDays = new Date(monthMeta.year, monthMeta.month, 0).getDate();
      return new Set(Array.from({ length: totalDays }, (_, index) => index + 1));
    }

    if (monthMeta.year === currentYear && monthMeta.month === currentMonth) {
      return new Set(Array.from({ length: Math.max(0, now.getDate() - 1) }, (_, index) => index + 1));
    }

    return new Set();
  }, [monthMeta]);

  const getDayLabel = (day) => {
    if (!monthMeta.year || !monthMeta.month) return "";
    const idx = new Date(monthMeta.year, monthMeta.month - 1, day).getDay();
    return dayLabels[idx] || "";
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMonth, selectedDepartment, selectedDesignation, selectedEmployeePrefix, searchTerm, perPage]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      const employeeId = normalizeEmployeeId(emp.employeeID);
      if (isExEmployeeId(employeeId)) return false;

      const scopedDeptMatch = !isDepartmentHeadScoped || emp.departmentName === scopedDepartment;
      const scopedDesignationMatch = !isDepartmentHeadScoped || scopedDesignations.length === 0 || scopedDesignations.includes(emp.designationName);

      const deptMatch = selectedDepartment === "ALL" || emp.departmentName === selectedDepartment;
      const designationMatch = selectedDesignation === "ALL" || emp.designationName === selectedDesignation;
      const prefixMatch = selectedEmployeePrefix === "ALL" || getEmployeePrefix(employeeId) === selectedEmployeePrefix;

      const q = String(searchTerm || "").toUpperCase().trim();
      if (!q) {
        return scopedDeptMatch && scopedDesignationMatch && deptMatch && designationMatch && prefixMatch;
      }

      const fullName = `${emp.firstName || ""} ${emp.middleName || ""} ${emp.lastName || ""}`.replace(/\s+/g, " ").trim().toUpperCase();
      const searchEmployeeId = String(emp.employeeID || "").toUpperCase();
      const employeeUserId = String(emp.employeeUserId || "").toUpperCase();
      const searchMatch = fullName.includes(q) || searchEmployeeId.includes(q) || employeeUserId.includes(q);

      return scopedDeptMatch && scopedDesignationMatch && deptMatch && designationMatch && prefixMatch && searchMatch;
    });
  }, [
    employees,
    isDepartmentHeadScoped,
    scopedDepartment,
    scopedDesignations,
    selectedDepartment,
    selectedDesignation,
    selectedEmployeePrefix,
    searchTerm,
  ]);

const handleShiftChange = (emp, day, value, isSecondHalf = null) => {
    if (expiredDaysSet.has(day) || (lockedDaysByUserId?.[emp.employeeUserId] || []).includes(day)) return;
    
    const normalizedValue = normalizeCode(value);
    const empShifts = shifts[emp.employeeUserId] || {};

    // Check OFF limit (Max 5)
    if (normalizedValue === "OFF") {
      let offCount = 0;
      Object.entries(empShifts).forEach(([d, val]) => {
        if (Number(d) !== Number(day) && normalizeCode(val) === "OFF") {
          offCount++;
        }
      });
      if (offCount >= 5) {
        toast.error("You already have 5 OFF added. Cannot add more!");
        return;
      }
    }

    // Check OFF(EXCH) limit (Max 5)
    if (normalizedValue === "OFF(EXCH)") {
      let offExchCount = 0;
      Object.entries(empShifts).forEach(([d, val]) => {
        if (Number(d) !== Number(day) && normalizeCode(val) === "OFF(EXCH)") {
          offExchCount++;
        }
      });
      if (offExchCount >= 5) {
        toast.error("You already have 5 OFF(EXCH) added. Cannot add more!");
        return;
      }
    }

    setShifts((prev) => {
      const currentEmpShifts = { ...(prev[emp.employeeUserId] || {}) };
      const currentVal = normalizeCode(currentEmpShifts[day] || "");
      
      if (normalizedValue === "DD") {
        const def = normalizeCode(nonDDShiftOptions[0]?.code || "M");
        currentEmpShifts[day] = encodeDDCode(def, def);
      } else if (isSecondHalf !== null && currentVal.startsWith("DD:")) {
        const [first, second] = parseDDCodes(currentVal);
        if (isSecondHalf) {
          currentEmpShifts[day] = encodeDDCode(first, normalizedValue);
        } else {
          currentEmpShifts[day] = encodeDDCode(normalizedValue, second || normalizedValue);
        }
      } else {
        currentEmpShifts[day] = normalizedValue;
      }

      return { ...prev, [emp.employeeUserId]: currentEmpShifts };
    });
  };

  const startIndex = perPage === "all" ? 0 : (currentPage - 1) * perPage;
  const paginatedEmployees = useMemo(
    () =>
      perPage === "all"
        ? filteredEmployees
        : filteredEmployees.slice(startIndex, startIndex + perPage),
    [filteredEmployees, perPage, startIndex]
  );
  
  const selectedEmployeeSet = useMemo(
    () => new Set(selectedEmployees),
    [selectedEmployees]
  );

const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent double clicks while already saving
    setIsSubmitting(true);
    
    try {
      const formatMonth = (ym) => {
        const [y, m] = ym.split("-");
        return new Date(y, m - 1).toLocaleString("en-US", { month: "short" }) + "-" + y;
      };

      const dataToSave = filteredEmployees
        .map(emp => {
          const empShifts = shifts[emp.employeeUserId] || {}; 
          const nonEmptyShifts = Object.fromEntries(
            Object.entries(empShifts).map(([day, val]) => [
              day,
              normalizeCode(val)
            ]).filter(([_, shift]) => shift)
          );
          
          if (Object.keys(nonEmptyShifts).length === 0) return null;
          
          return {
            employeeID: emp.employeeID,
            employeeUserId: emp.employeeUserId,
            employeeName: `${emp.firstName} ${emp.middleName} ${emp.lastName}`,
            designation: emp.designationName,
            department: emp.departmentName,
            shifts: nonEmptyShifts,
          };
        })
        .filter(Boolean);

      if (dataToSave.length === 0) {
        toast.error("No shifts to save ❌");
        setIsSubmitting(false);
        return;
      }

      await axios.post("/api/shift-management/save-bulk", {
        month: formatMonth(selectedMonth), 
        data: dataToSave,
      });

      toast.success("Shift saved successfully ✅");
    } catch {
      toast.error("Failed to save shifts ❌");
    } finally {
      setIsSubmitting(false); // Always reset loading state when finished
    }
  };

  const EMP_COLORS = [
    "bg-slate-50",
    "bg-white",
    "bg-slate-50/50",
  ];

  const getRowColor = (index) => EMP_COLORS[index % EMP_COLORS.length];

  const getShiftColor = (shift, rowIndex) => {
    if (shift === "OFF" || shift === "OFF(EXCH)") return "bg-rose-50 text-rose-700"; 
    return getRowColor(rowIndex);
  };

  const handleExportExcel = () => {
    const exportRows =
      selectedEmployees.length > 0
        ? filteredEmployees.filter(emp =>
            selectedEmployees.includes(emp.employeeID)
          )
        : filteredEmployees;

    const exportData = exportRows.map((emp, index) => {
      const empShifts = shifts[emp.employeeUserId] || {};
      const row = {
        "SL No": index + 1,
        "Emp ID": emp.employeeID || "-",
        "User ID": emp.employeeUserId || "-",
        "Employee Name": `${emp.firstName || ""} ${emp.middleName || ""} ${emp.lastName || ""}`
          .replace(/\s+/g, " ")
          .trim(),
        "Department": emp.departmentName || "-",
        "Designation": emp.designationName || "-",
      };

      daysInMonth.forEach((day) => {
        const value = String(empShifts[day] || "").trim();
        row[`Day ${day}`] = value.startsWith("DD:") ? value.slice(3) : value || "-";
      });

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Shift Report");
    XLSX.writeFile(workbook, `Shift_Report_${selectedMonth}.xlsx`);
    toast.success("Excel exported successfully");
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 400; 
      tableContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Calendar size={14} /></div>
              <div><h1 className={masterTitle}>Shift Management</h1></div>
            </div>
            <div className="flex gap-2">
              <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`${masterAccentButton} ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin inline mr-1" size={10} />
                      PLEASE WAIT...
                    </>
                  ) : (
                    <>
                      <Save size={10} /> SUBMIT
                    </>
                  )}
                </button>
              <button
                type="button"
                onClick={handleExportExcel}
                className={masterPrimaryButton}
              >
                <Download size={10} /> EXPORT EXCEL
              </button>
              <BackButton />
            </div>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full flex flex-col flex-1 min-h-0`}>
          <MobileHeaderToggle>
            {/* Filter and Search Bar Section */}
            <div className={`${masterPanel} p-3 mb-2 flex flex-col gap-3`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Month</label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className={masterInput}
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Department</label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => {
                      setSelectedDepartment(e.target.value);
                      setSelectedDesignation("ALL");
                    }}
                    disabled={isDepartmentHeadScoped}
                    className={masterSelect}
                  >
                    {visibleDepartments.map((dept) => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Designation</label>
                  <select
                    value={selectedDesignation}
                    onChange={(e) => setSelectedDesignation(e.target.value)}
                    disabled={isDepartmentHeadScoped}
                    className={masterSelect}
                  >
                    {displayDesignations.map((des) => (
                      <option key={des} value={des}>{des}</option>
                    ))}
                  </select>
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Status</label>
                  <select
                    value={selectedEmployeePrefix}
                    onChange={(e) => setSelectedEmployeePrefix(e.target.value)}
                    className={masterSelect}
                  >
                    {employeePrefixOptions.map((prefix) => (
                      <option key={prefix} value={prefix}>{prefix}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-2">
                <SearchBar 
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(normalizeSearchInput(val));
                    setCurrentPage(1);
                  }} 
                  placeholder="Search by employee name, ID, or user ID..." 
                  totalItems={filteredEmployees.length} 
                  currentPage={currentPage} 
                  pageSize={perPage} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={(newSize) => {
                    setPerPage(newSize);
                    setCurrentPage(1);
                  }} 
                />
              </div>
            </div>
          </MobileHeaderToggle>

          <div className="relative group flex-1 flex flex-col min-h-0"> 
            <button 
              type="button"
              onClick={() => scrollTable('left')}
              className="fixed left-[280px] top-1/2 -translate-y-1/2 z-50 text-slate-600 bg-white/80 hover:bg-white shadow-md p-2 rounded-full transition-all active:scale-90 hidden md:block"
              style={{ marginLeft: '10px' }}
            >
              <span className="text-xl font-bold">❮</span>
            </button>

            <button 
              type="button"
              onClick={() => scrollTable('right')}
              className="fixed right-6 top-1/2 -translate-y-1/2 z-50 text-slate-600 bg-white/80 hover:bg-white shadow-md p-2 rounded-full transition-all active:scale-90 hidden md:block"
            >
              <span className="text-xl font-bold">❯</span>
            </button>

            {/* Scrollable Container with Mobile Support */}
            <div 
              ref={tableContainerRef} 
              className={`${masterPanel} w-full flex-1 min-h-0 overflow-x-auto overflow-y-auto scroll-smooth touch-pan-x`}
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <table className={`${masterTable} min-w-[max-content]`}>
                <thead>
                  <tr className={masterStickyHeadRow}>
                    <th className={masterTableData} style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={
                          filteredEmployees.length > 0 &&
                          selectedEmployees.length === filteredEmployees.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEmployees(
                              filteredEmployees.map(emp => emp.employeeID)
                            );
                          } else {
                            setSelectedEmployees([]);
                          }
                        }}
                      />
                    </th>
                    <th className={masterTableData} style={{ width: '50px' }}>SL No</th>
                    <th className={masterTableData}>Emp ID</th>
                    <th className={masterTableData}>User ID</th>
                    <th className={masterTableData}>Employee Name</th>
                    <th className={masterTableData}>Department</th>
                    <th className={masterTableData}>Designation</th>
                    {daysInMonth.map((day) => (
                      <th key={day} className={`${masterTableData} text-center min-w-[50px]`}>
                        <div className="flex flex-col items-center leading-tight">
                          <span>{day}</span>
                          <span className="text-[9px] font-normal text-slate-300">
                            {getDayLabel(day)}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={masterTbody}>
                  {paginatedEmployees.map((emp, index) => (
                    <tr key={emp.employeeUserId} className={`${masterTableRow} ${getRowColor(index)}`}>
                      <td className={masterTableCell + " text-center"}>
                        <input
                          type="checkbox"
                          checked={selectedEmployeeSet.has(emp.employeeID)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEmployees(prev => [...prev, emp.employeeID]);
                            } else {
                              setSelectedEmployees(prev =>
                                prev.filter(id => id !== emp.employeeID)
                              );
                            }
                          }}
                        />
                      </td>
                      <td className={masterTableCell}>{perPage === "all" ? index + 1 : startIndex + index + 1}</td>
                      <td className={masterTableCell}>{emp.employeeID}</td>
                      <td className={masterTableCell}>{emp.employeeUserId}</td>
                      <td className={masterTableCell}>{emp.firstName} {emp.middleName} {emp.lastName}</td>
                      <td className={masterTableCell}>{emp.departmentName}</td>
                      <td className={masterTableCell}>{emp.designationName}</td>
                      {daysInMonth.map((day) => {
                        const currentShift = shifts?.[emp.employeeUserId]?.[day] || "";
                        const isDD = currentShift.startsWith("DD:");
                        const ddParts = isDD ? parseDDCodes(currentShift) : ["", ""];
                        const isLocked =
                          expiredDaysSet.has(day) ||
                          (lockedDaysByUserId?.[emp.employeeUserId] || []).includes(day);

                        return (
                          <td
                            key={day}
                            className={`${masterTableCell} text-center p-0.5 ${getShiftColor(isDD ? "DD" : currentShift, index)}`}
                          >
                            <div className="flex flex-col items-center gap-0.5">
                              <select
                                value={isDD ? "DD" : currentShift}
                                onChange={(e) => handleShiftChange(emp, day, e.target.value)}
                                disabled={isLocked}
                                className={`bg-transparent border border-slate-300 rounded px-0.5 py-0.5 text-[10px] font-semibold w-full ${isLocked ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
                                title={isLocked ? "Attendance already exists for this date. Shift cannot be changed." : ""}
                              >
                                <option value="">-</option>
                                {shiftOptions.map((opt) => (
                                  <option 
                                    key={opt.code} 
                                    value={opt.code}
                                    title={opt.name && opt.start ? `${opt.name}: ${opt.start} - ${opt.end}` : opt.name}
                                  >
                                    {opt.code}
                                  </option>
                                ))}
                              </select>

                              {isDD && (
                                <div className="flex items-center gap-0.5 border-t border-slate-200 pt-0.5 w-full justify-center">
                                  <select
                                    value={ddParts[0]}
                                    onChange={(e) => handleShiftChange(emp, day, e.target.value, false)}
                                    disabled={isLocked}
                                    className={`bg-white border border-slate-300 rounded text-[9px] w-9 px-0 font-bold ${isLocked ? "cursor-not-allowed opacity-60" : ""}`}
                                  >
                                    {nonDDShiftOptions.map(opt => (
                                      <option 
                                        key={opt.code} 
                                        value={opt.code} 
                                        title={`${opt.name}: ${opt.start} - ${opt.end}`}
                                      >
                                        {opt.code}
                                      </option>
                                    ))}
                                  </select>

                                  <span className="text-[9px] font-bold">+</span>

                                  <select
                                    value={ddParts[1]}
                                    onChange={(e) => handleShiftChange(emp, day, e.target.value, true)}
                                    disabled={isLocked}
                                    className={`bg-white border border-slate-300 rounded text-[9px] w-9 px-0 font-bold ${isLocked ? "cursor-not-allowed opacity-60" : ""}`}
                                  >
                                    {nonDDShiftOptions.map(opt => (
                                      <option 
                                        key={opt.code} 
                                        value={opt.code} 
                                        title={`${opt.name}: ${opt.start} - ${opt.end}`}
                                      >
                                        {opt.code}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

        
          </div>
        </main>
      </div>
    </div>
  );
};

export default ShiftManagement;